import { Component, OnInit, ViewChild, ElementRef, OnDestroy } from '@angular/core';
import { NgForm } from '@angular/forms';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import _ from 'lodash-es';
import { StudyService } from '../../services/study.service';
import { DialogService } from '../../services/dialog.service';
import { StudyResult } from '../../models/studyResult.model';
import * as Constants from '@shared/_constants';
import {
  TabSteps,
  StudyStatus,
  FinancialResultsFormattingLookupValues,
  StudySettingsLookupValues,
  FYAlignmentMethod,
  BasePLIVendorAccount,
} from '../_study_constants';
import { StudyAccessInfoComponent } from '../study-crud/studyAccessInfo.component';
import { StudyViewModel } from '../../viewmodel/study.viewmodel';
import { StudyViewModelFactory } from '../../viewmodel/study.viewmodelfactory';
import { LoggingService } from '@services/logging.service';
import { forkJoin, Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LeverageType } from './leverage-type.model';
import { FinancialReviewService } from '../../services/financial-review.service';
import { AppState } from '../../reducers';
import { select, Store } from '@ngrx/store';
import { selectSearchStrategyId } from '../analysis/search-strategy/state/study-search-strategy.selectors';
import { StudyType } from '../../mystudies/study-crud/study-type.model';
import { StudyDetailService } from '@services/study-detail.service';
import { Study } from '../../models/study.model';

@Component({
  selector: 'app-newstudy-dialog',
  templateUrl: './newstudy-dialog.component.html',
  styleUrls: ['./newstudy-dialog.component.css'],
})
export class NewStudyDialogComponent implements OnInit, OnDestroy {
  @ViewChild(StudyAccessInfoComponent, { static: false })
  private studyAccessInfoComponent: StudyAccessInfoComponent;

  newStudy: StudyViewModel = new StudyViewModel();
  errorMessage: string;
  showErrorMessage = false;
  submitClicked = false;
  studyInfoValid = false;
  testedPartyInfoValid = false;
  studyAccessInfoValid = false;
  reviewSubmitValid = false;
  selectedTab = TabSteps.studyInfo;
  tabsteps = TabSteps;
  formCompleted = false;
  isNewStudyIndustryBlank = false;
  // allow anavigation between tab using tab links (enabled on development mode only)
  developmentMode = true;
  showLoader = false;

  // leveraging a study
  leveragedStudyId: number = null;
  sourceStudyIsLegacy = false;

  studySetup: LeverageType = LeverageType.StudySetup;
  studySetupWithSearchStrategy: LeverageType = LeverageType.StudySetupWithSearchStrategy;
  studySetupWithSearchStrategyAndRestatements: LeverageType = LeverageType.StudySetupWithSearchStrategyAndRestatements;

  selectedLeverageType: LeverageType = LeverageType.StudySetup;
  hasReclassItems = false;

  searchStrategyId: number;

  warnOnModify = false;
  isAIEnableDisableToggleOnYes = true;

  destroy$: Subject<boolean> = new Subject<boolean>();

  studyType = StudyType;
  constructor(
    private logger: LoggingService,
    private viewModelFactory: StudyViewModelFactory,
    private studyService: StudyService,
    private financialReviewService: FinancialReviewService,
    private dialogService: DialogService,
    private activeModal: NgbActiveModal,
    private _elRef: ElementRef,
    private store: Store<AppState>,
    private studyDetailService: StudyDetailService,
  ) {}

  public async ngOnInit() {
    this.store
      .pipe(takeUntil(this.destroy$))
      .pipe(select(selectSearchStrategyId))
      .subscribe((id) => {
        this.searchStrategyId = id;
      });

    this.showLoader = true;

    if (this.leveragedStudyId) {
      this.selectedTab = TabSteps.leverageStudy;
      this.tabsteps = TabSteps;
      this.warnOnModify = true;
    }

    const calls$: Observable<any>[] = [];
    calls$.push(this.viewModelFactory.initializeViewModel());

    if (this.searchStrategyId) {
      calls$.push(this.financialReviewService.isAnyReclassItemAdded(this.searchStrategyId));
    }

    forkJoin(calls$)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (res: any) => {
          const [_initResult, _hasReclassItems] = res;
          this.initializeSetup(_initResult);
          this.hasReclassItems = _hasReclassItems;
          this.newStudy = _.cloneDeep(this.newStudy);
          this.showLoader = false;
        },
        (err) => {
          console.log(err);
          this.showLoader = false;
        },
      );
  }

  initializeSetup(result: any) {
    this.newStudy = result; //here we get all the dropdown values Ex. Project Office Countries, Fiscal Years etc.

    this.newStudy.study.displayStartDate = new Date();
    if (this.leveragedStudyId) {
      // Leverage Study - load default values
      this.studyService.GetStudyForLeverage$(this.leveragedStudyId).subscribe((res) => {
        const leveragedStudy = res;
        this.newStudy.study.periodOfStudyFrom = leveragedStudy.periodOfStudyFrom;
        this.newStudy.study.periodOfStudyTo = leveragedStudy.periodOfStudyTo;
        this.newStudy.study.fyAlignmentMethodID = leveragedStudy.fyAlignmentMethodID || FYAlignmentMethod.March31;
        this.newStudy.study.displaytestedPartyFiscalYearEnd = leveragedStudy.testedPartyFiscalYearEnd
          ? new Date(leveragedStudy.testedPartyFiscalYearEnd)
          : new Date();
        this.newStudy.study.monthsBack = leveragedStudy.monthsBack || 7;
        this.newStudy.study.useFunctionalCurrency = leveragedStudy.useFunctionalCurrency;
        this.newStudy.study.currencyId = leveragedStudy.currencyId;
        this.newStudy.study.currencyUnitId = leveragedStudy.currencyUnitId;
        this.newStudy.study.financialResultFormatId = leveragedStudy.financialResultFormatId;
        this.newStudy.study.studySettingId = leveragedStudy.studySettingId;
        this.newStudy.study.averagingMethodID = leveragedStudy.averagingMethodID;
        this.newStudy.study.defaultBasePLIVendorAccountId = leveragedStudy.defaultBasePLIVendorAccountId;
        this.newStudy.study.translateNativeLanguageTD = leveragedStudy.translateNativeLanguageTD;
        this.newStudy.study.isAISuggestionsEnabled = leveragedStudy.isAISuggestionsEnabled;
        this.newStudy.study.leveragedStudyId = this.leveragedStudyId;
        this.newStudy.study.isDemoStudy = leveragedStudy.isDemoStudy;

        leveragedStudy.iqrValues.forEach((element) => {
          if (this.newStudy.selectedRatios.find((z) => z === element.ratioId) === undefined) {
            this.newStudy.selectedRatios.push(element.ratioId);
          }
        });
      });
    } else {
      // By default, EYQuartileRange to be get selected.
      this.newStudy.study.financialResultFormatId = FinancialResultsFormattingLookupValues.EYQuartileRange;

      // By default, FullOverview to be get selected.
      this.newStudy.study.studySettingId = StudySettingsLookupValues.AllAvailable;

      // Default the value of FYAlignmentMethodId
      this.newStudy.study.fyAlignmentMethodID = FYAlignmentMethod.March31;
      this.newStudy.study.defaultBasePLIVendorAccountId = BasePLIVendorAccount.OperatingRevenue;
      this.newStudy.study.displaytestedPartyFiscalYearEnd = new Date();
      this.newStudy.study.monthsBack = 7;
    }
  }

  public async save(form: NgForm) {
    this.logger.info(this.constructor.name, 'save', 'Start');
    try {
      this.showLoader = true;
      this.showErrorMessage = false;
      this.errorMessage = '';
      this.submitClicked = true;
      const hasMinTwoUsersAC = this.newStudy == null && _.isEmpty(this.newStudy.userList) ? false : this.newStudy.userList.length > 1;

      const isformValid = form.valid && this.studyInfoValid && hasMinTwoUsersAC;
      if (isformValid) {
        // set study status to In-Progress
        this.newStudy.study.statusId = StudyStatus.InProgress;

        // convert list into array, to set User study info for study model
        this.newStudy.study.userStudies = _.filter(this.newStudy.userList, 'isActive');

        // convert list into array, to set benchmark country info for study model
        this.newStudy.selectedBenchmarkCountries.forEach((element) => {
          this.newStudy.study.benchmarkCountries.push({ countryId: parseInt(element, 10), country: null });
        });

        this.newStudy.selectedRatios.forEach((element) => {
          this.newStudy.study.iqrValues.push({ studyId: this.newStudy.study.studyId, ratioId: element, isActive: true });
        });

        this.newStudy.study.iqrValues = this.newStudy.study.iqrValues.filter(function (x, i, a) {
          return a.indexOf(x) === i;
        });

        // convert list into array, to set tested party info for study model
        this.newStudy.selectedTestedPartyCountries.forEach((element) => {
          this.newStudy.study.testedPartyCountries.push({ regionCountryId: parseInt(element, 10) });
        });

        // todatestring() is used, as while saving date in db it converts to UTC and while selecting date it picks up local time which create issue while saving date.
        if (this.newStudy.study.displayStartDate) {
          this.newStudy.study.startDate = this.newStudy.study.displayStartDate.toDateString();
        } else {
          this.newStudy.study.startDate = null;
        }
        if (this.newStudy.study.displayEndDate) {
          this.newStudy.study.endDate = this.newStudy.study.displayEndDate.toDateString();
        } else {
          this.newStudy.study.endDate = null;
        }
        if (this.newStudy.study.displaytestedPartyFiscalYearEnd) {
          this.newStudy.study.testedPartyFiscalYearEnd = this.newStudy.study.displaytestedPartyFiscalYearEnd.toDateString();
        } else {
          this.newStudy.study.testedPartyFiscalYearEnd = null;
        }

        if (!this.newStudy.study.studyTypeId) {
          this.newStudy.study.studyTypeId = this.newStudy.study.isDemoStudy ? this.studyType.Test : this.studyType.NonStandard;
        }

        // Inherit isLegacyStudy from the source study
        this.newStudy.study.isLegacyStudy = this.sourceStudyIsLegacy;

        const savedStudy = await this.studyService.Save(this.newStudy.study);

        this.intiStudyDetailServiceForleveragedStudy(this.newStudy.study);

        if (
          this.selectedLeverageType === LeverageType.StudySetupWithSearchStrategy ||
          this.selectedLeverageType === LeverageType.StudySetupWithSearchStrategyAndRestatements
        ) {
          const copyReclass = this.selectedLeverageType === LeverageType.StudySetupWithSearchStrategyAndRestatements;
          if (this.sourceStudyIsLegacy)
            await this.studyService.processLeveragedLegacyStudy(this.leveragedStudyId, savedStudy.studyId, copyReclass);
          else
            await this.studyService.processLeveragedStudy(this.leveragedStudyId, savedStudy.studyId, copyReclass);
        }

        if (savedStudy) {
          this.newStudy.study = savedStudy;
          this.formCompleted = true;
          this.errorMessage = '';
          this.showErrorMessage = false;
        } else {
          this.errorMessage = Constants.ResultMessage.ErrorEncountered;
          this.showErrorMessage = true;
        }
      }
    } finally {
      this.showLoader = false;
    }
  }

  get studySetupOnlySelected() {
    return this.selectedLeverageType === LeverageType.StudySetup;
  }

  public nextTab(form: any) {
    this.logger.info(this.constructor.name, 'nextTab', 'Start');
    const activeId = this.selectedTab;
    this.submitClicked = true;
    let formValid = false;
    if (form.valid && !this.isNewStudyIndustryBlank) {
      this.submitClicked = false;
      switch (activeId) {
        case TabSteps.studyInfo: {
          const studyperiodChecking = this.newStudy.checkPeriodEnding(this.newStudy.study.periodOfStudyFrom, this.newStudy.study.periodOfStudyTo);
          const studyPeriodTimeFrameChecking = this.newStudy.checkPeriodTimeFrame(
            this.newStudy.study.periodOfStudyFrom,
            this.newStudy.study.periodOfStudyTo,
          );
          const studyDateChecking = this.newStudy.checkStudyDate();
          if (this.newStudy.selectedBenchmarkCountries.length === 0 || studyperiodChecking || studyPeriodTimeFrameChecking || studyDateChecking) {
            this.studyInfoValid = false;
            formValid = false;
            this.submitClicked = true;
          } else {
            this.studyInfoValid = true;
            formValid = true;
            this.submitClicked = true;
          }
          break;
        }
        case TabSteps.studyAccess:
          if (!this.studyAccessInfoComponent.IsValidTeamComposition()) {
            this.studyAccessInfoValid = false;
            formValid = false;
          } else {
            this.studyAccessInfoValid = true;
            formValid = true;
            this.submitClicked = true;
          }
          break;
      }

      if (formValid) {
        this.selectedTab = activeId + 1;
        this.submitClicked = false; // invalidate for the next tab
        this.scrollToTop();
      }
    }
  }

  public backTab() {
    const activeId = this.selectedTab;
    let selectedActiveId = 0;

    // TODO: Need to remove hardcoded value
    if (activeId > 0 && activeId <= 4) {
      selectedActiveId = activeId - 1;
    } else {
      selectedActiveId = 0;
    }
    this.selectedTab = selectedActiveId;
    this.scrollToTop();
  }

  public async close() {
    this.logger.info(this.constructor.name, 'nextTab', 'Start');

    const newStudyResult: StudyResult = { result: Constants.NewStudyDialogResult.cancelled };

    if (this.selectedTab === TabSteps.leverageStudy) {
      // leverage study, short cut, just close it
      // Task 65234: Leverage Study - if you Cancel on first step, simply close dialog, do not prompt to save changes
      this.activeModal.close(newStudyResult);
      return;
    }

    const studyChanged = this.studyService.JSONStringify(new StudyViewModel()) !== this.studyService.JSONStringify(this.newStudy);

    if (studyChanged === true && !this.formCompleted) {
      const confirmationResult = await this.dialogService.confirm('Please Confirm', Constants.ConfirmationMessage.UnsavedModifications, 'Yes', 'No');
      if (confirmationResult === Constants.ConfirmationResult.yes) {
        this.activeModal.close(newStudyResult);
      }
    } else if (this.formCompleted) {
      this.redirectToMyStudies();
    } else {
      this.activeModal.close(newStudyResult);
    }
  }

  public redirectToQuantitativeSearch() {
    if (this.sourceStudyIsLegacy) {
      this.studyService.AddAndCheckLegacyStudyViews(this.newStudy.study.studyId);
    }
    else {
      this.studyService.AddAndCheckStudyViews(this.newStudy.study.studyId);
    }
    const newStudyResult: StudyResult = { result: Constants.NewStudyDialogResult.goToQuantitativeSearch, newStudyId: this.newStudy.study.studyId };
    this.activeModal.close(newStudyResult);
  }

  public redirectToMyStudies() {
    const newStudyResult: StudyResult = { result: Constants.NewStudyDialogResult.goToMyStudies, newStudyId: this.newStudy.study.studyId };
    this.activeModal.close(newStudyResult);
  }

  public redirectToSearchStudies() {
    const newStudyResult: StudyResult = { result: Constants.NewStudyDialogResult.goToSearchStudies, newStudyId: this.newStudy.study.studyId };
    this.activeModal.close(newStudyResult);
  }

  confirm() {
    this.selectedTab = TabSteps.studyInfo;
  }

  private scrollToTop(): void {
    this._elRef.nativeElement.querySelector('.ey-wizard-steps .nav').scrollIntoView();
  }

  private intiStudyDetailServiceForleveragedStudy(study: Study) {
    if (this.leveragedStudyId) {
      this.studyDetailService.study.statusId = study.statusId;
      this.studyDetailService.studyStatusChanged();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
  }
}
